/**
 * Urun detayi.
 *
 * ── YETKISIZ KULLANICI DA GORUR ──────────────────────────────────────────────
 * Ama farkli bir sayfa gorur: indirme yerine "Bilgi al", surum listesi yerine
 * ozet. Yetkisiz kullaniciya bos bir "erisim yok" ekrani gostermek satis
 * firsatini oldurur; her seyi gostermek de yaniltir. Ortasi: vitrin + net kilit.
 *
 * ── INDIRME MASAUSTUNDEN ─────────────────────────────────────────────────────
 * Tarayici urun indirmez. Icerik sifreli ve cihaza bagli; cozme anahtari
 * yalnizca Cortex'e, gecerli makbuz karsiligi veriliyor
 * (bkz. docs/CONTENT-PROTECTION.md). Bu yuzden web'de "Cortex'te aç" var.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Badge,
  Card,
  ErrorState,
  LoadingBlock,
  TableWrap,
} from '@/components/ui';
import { canonicalCatalogName, formatBytes, formatDate, PRODUCT_KIND_LABEL, ENDPOINT_KIND_LABEL } from '@/lib/format';
import { catalogBanner, catalogCover, productCopy } from '@/lib/gameCatalog';
import { copy as localizedCopy, localizeCatalogName, useI18n } from '@/lib/i18n';
import { quoteRequestPath } from '@/lib/quote';
import { useToast } from '@/lib/toast';
import type { CatalogItem, ProductDetail as ProductDetailType, ProductLocalizedLabel } from '@shared/index';
import './CustomerPages.css';

export default function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useI18n();
  const c = (tr: string, en: string) => localizedCopy(language, tr, en);
  const toast = useToast();

  const product = useQuery({
    queryKey: ['product', slug, language],
    queryFn: () => api.get<ProductDetailType>(`/catalog/${slug}`),
    enabled: Boolean(slug),
  });
  const related = useQuery({
    queryKey: ['catalog', 'related', product.data?.category, language],
    queryFn: () => api.get<{ items: CatalogItem[] }>('/catalog', { category: product.data!.category }),
    enabled: Boolean(product.data?.category),
  });
  const liveDemo = useMutation({
    mutationFn: () => api.post<{ url: string }>(`/catalog/${slug}/live-demo`, {}),
    onError: () => toast.error(c('Canlı demo başlatılamadı.', 'Live demo could not be started.')),
  });

  if (product.isLoading) return <LoadingBlock rows={5} label={c('Ürün yükleniyor', 'Loading product')} />;
  if (product.isError || !product.data) {
    return (
      <ErrorState
        message={c('Ürün bulunamadı veya erişilemedi.', 'Product could not be found or accessed.')}
        onRetry={() => void product.refetch()}
      />
    );
  }

  const p = product.data;
  const productText = productCopy(p, language);
  const banner = catalogBanner(p);
  const cover = catalogCover(p);
  const gallery = galleryFor(p, banner, cover);
  const facts = factsFor(p, language, c);
  const relatedItems = (related.data?.items ?? []).filter((item) => item.id !== p.id).slice(0, 3);
  const req = p.requirements ?? {};
  const hasRequirements =
    Boolean(req.endpointKinds?.length) ||
    Boolean(req.aspectRatios?.length) ||
    Boolean(req.minWidth) ||
    Boolean(req.minHeight) ||
    req.requiresAudio === true ||
    req.requiresDedicatedGpu === true ||
    Boolean(req.minDedicatedGpuMemoryMb) ||
    Boolean(req.minRefreshRateHz) ||
    Boolean(req.maxApiLatencyMs);

  return (
    <div className="product-page stack stack--lg">
      <nav className="product-page__crumbs" aria-label={c('İçerik yolu', 'Content path')}>
        <Link to="/kesfet">{c('Keşfet', 'Discover')}</Link><span aria-hidden="true">/</span><span>{localizeCatalogName(p.category, language)}</span>
      </nav>

      <section className="product-hero">
        <ProductMediaGallery key={`${p.id}:${language}`} images={gallery} name={p.name} c={c} />
        <div className="product-hero__info">
          <div className="product-hero__badges">
            <Badge tone="primary">{language === 'tr' ? PRODUCT_KIND_LABEL[p.kind] ?? p.kind : productKindEnglish(p.kind)}</Badge>
            <Badge>{localizeCatalogName(p.category, language)}</Badge>
          </div>
          <h1>{p.name}</h1>
          <p className="product-hero__summary">{productText.summary}</p>
          {productText.highlights.length > 0 && (
            <ul className="product-hero__highlights">
              {productText.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
            </ul>
          )}
          <div className="product-hero__state">
            {p.entitled ? <Badge tone="success">{c('Hesabınıza tanımlı', 'Assigned to your account')}</Badge> : <span className="small muted">{c('Bu içerik henüz hesabınıza tanımlı değil.', 'This content is not assigned to your account yet.')}</span>}
            {p.latestVersion && <Badge>v{p.latestVersion}</Badge>}
            {p.sizeBytes && <Badge>{formatBytes(p.sizeBytes)}</Badge>}
          </div>
          <div className="product-hero__action">
            {p.entitled ? (
              p.delivery === 'desktop-install' ? (
                <div className="product-hero__action-stack">
                  {p.requirements?.liveDemoEnabled && (
                    <button
                      type="button"
                      className="btn btn--primary btn--lg product-hero__live-demo"
                      disabled={liveDemo.isPending}
                      onClick={() => {
                        // İstek asenkron tamamlandığı için yeni sekmeyi tıklama anında
                        // açmak gerekir; aksi halde popup engelleyici devreye girer.
                        // `noopener` özelliğini window.open'a vermek bazı tarayıcılarda
                        // dönüş değerini null yapıp mevcut Hub sekmesini yönlendiriyordu.
                        const demoWindow = window.open('about:blank', '_blank');
                        if (demoWindow) demoWindow.opener = null;
                        liveDemo.mutate(undefined, {
                          onSuccess: ({ url }) => {
                            if (demoWindow) demoWindow.location.href = url;
                            else window.location.assign(url);
                          },
                          onError: () => demoWindow?.close(),
                        });
                      }}
                    >
                      {liveDemo.isPending ? c('Demo hazırlanıyor…', 'Preparing demo…') : c('Canlı Demo', 'Live demo')}
                      <span aria-hidden="true">↗</span>
                    </button>
                  )}
                  {p.latestVersion ? (
                    <>
                      <a
                        className="btn btn--primary btn--lg"
                        href={`cortex://product/${encodeURIComponent(p.slug)}`}
                      >
                        {c("CORTEX'te aç", 'Open in CORTEX')}
                      </a>
                      <div className="product-hero__cortex"><strong>{c('CORTEX kitaplığında hazır', 'Ready in your CORTEX library')}</strong><span>{c('Masaüstü uygulamasından yüklenir ve çalıştırılır.', 'Install and launch it from the desktop app.')}</span></div>
                    </>
                  ) : (
                    <>
                      <button type="button" className="btn btn--primary btn--lg" disabled>
                        {c('CORTEX sürümü hazırlanıyor', 'CORTEX release in preparation')}
                      </button>
                      <div className="product-hero__cortex"><strong>{c('Henüz indirilebilir paket yok', 'No downloadable package yet')}</strong><span>{c('Canlı ve doğrulanmış sürüm yayınlandığında bu düğme otomatik açılır.', 'This button becomes available automatically after a verified release is published.')}</span></div>
                    </>
                  )}
                </div>
              ) : (
                <Link className="btn btn--primary btn--lg" to="/operasyon/ekranlar">{c('Operasyona git', 'Go to operations')}</Link>
              )
            ) : canonicalCatalogName(p.category) === 'Leds Play' ? (
              <div className="stack stack--sm">
                <p>{c('Bu oyun Leds Play paketine dahildir. Teklif, katalogdaki tüm oyunlar için hazırlanır.', 'This game is included in the Leds Play package. Quotes cover all games in the catalog.')}</p>
                <Link className="btn btn--primary btn--lg" to="/kesfet?kategori=Leds+Play">{c('Leds Play paketini incele', 'Explore the Leds Play package')}</Link>
              </div>
            ) : (
              <Link className="btn btn--primary btn--lg" to={quoteRequestPath({ solution: p.name, catalog: p.category, source: `/kesfet/${p.slug}` })}>{c('Teklif Al', 'Request a Quote')}</Link>
            )}
          </div>
        </div>
      </section>

      <section className="product-facts" aria-label={c('Deneyim künyesi', 'Experience facts')}>
        {facts.map((fact, index) => (
          <article key={fact.label}>
            <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
            <div>
              <small>{fact.label}</small>
              <strong>{fact.value}</strong>
            </div>
          </article>
        ))}
      </section>

      <div className="product-page__overview">
        <Card title={c('Deneyim hakkında', 'About this experience')}>
          <p style={{ whiteSpace: 'pre-wrap' }}>{productText.description || productText.summary}</p>
        </Card>
        <Card title={c('Nasıl teslim edilir?', 'How is it delivered?')}>
          {p.delivery === 'desktop-install' ? (
            <p>{c('CORTEX gerekli çalışma bileşenlerini arka planda hazırlar. Müşteri yalnızca lisanslı içeriğini görür, yükler ve tanımlı bilgisayarda çalıştırır.', 'CORTEX prepares required runtime components in the background. Customers see and install only their licensed content, then run it on an assigned computer.')}</p>
          ) : (
            <p>{c('İçerik Hub üzerinden cihaz veya deneyim noktasına atanır; oturumlar ve çalışma durumu operasyon alanından yönetilir.', 'Content is assigned to a device or experience point through Hub; sessions and runtime status are managed from operations.')}</p>
          )}
        </Card>
      </div>

      {hasRequirements && (
        <Card title={c('Çalışma koşulları', 'Runtime requirements')}>
          <p className="small muted" style={{ marginBottom: 'var(--space-4)' }}>
            {c('Bu ürün yalnızca aşağıdaki koşulları sağlayan noktalarda planlanabilir. Uyumsuz bir nokta seçerseniz takvim sizi uyarır.', 'This product can only be scheduled at points that meet the following requirements. The calendar warns you if you choose an incompatible point.')}
          </p>
          <dl className="stack stack--sm">
            {req.endpointKinds?.length ? (
              <Row
                label={c('Nokta türü', 'Point type')}
                value={req.endpointKinds.map((k) => language === 'tr' ? ENDPOINT_KIND_LABEL[k] ?? k : endpointKindEnglish(k)).join(', ')}
              />
            ) : null}
            {req.aspectRatios?.length ? (
              <Row label={c('Ekran oranı', 'Aspect ratio')} value={req.aspectRatios.join(c(' veya ', ' or '))} />
            ) : null}
            {req.minWidth ? <Row label={c('En az genişlik', 'Minimum width')} value={`${req.minWidth} ${c('piksel', 'pixels')}`} /> : null}
            {req.minHeight ? <Row label={c('En az yükseklik', 'Minimum height')} value={`${req.minHeight} ${c('piksel', 'pixels')}`} /> : null}
            {req.requiresAudio ? <Row label={c('Ses', 'Audio')} value={c('Gerekli', 'Required')} /> : null}
            {req.requiresDedicatedGpu ? <Row label={c('GPU', 'GPU')} value={c('Ayrılmış GPU gerekli', 'Dedicated GPU required')} /> : null}
            {req.minDedicatedGpuMemoryMb ? <Row label={c('GPU belleği', 'GPU memory')} value={`${req.minDedicatedGpuMemoryMb} MB ${c('ve üzeri', 'or more')}`} /> : null}
            {req.minRefreshRateHz ? <Row label={c('Yenileme hızı', 'Refresh rate')} value={`${req.minRefreshRateHz} Hz ${c('ve üzeri', 'or more')}`} /> : null}
            {req.maxApiLatencyMs ? <Row label={c('Hub gecikmesi', 'Hub latency')} value={`${req.maxApiLatencyMs} ms ${c('ve altı', 'or less')}`} /> : null}
          </dl>
        </Card>
      )}

      {relatedItems.length > 0 && (
        <section className="product-related" aria-labelledby="product-related-title">
          <header>
            <div>
              <span>{c('AYNI KATALOGDAN', 'FROM THE SAME CATALOG')}</span>
              <h2 id="product-related-title">{c('Diğer deneyimler', 'Other experiences')}</h2>
            </div>
            <Link to={`/kesfet?kategori=${encodeURIComponent(p.category)}`}>{c('Kataloğu görüntüle', 'View catalog')} <span aria-hidden="true">→</span></Link>
          </header>
          <div className="product-related__grid">
            {relatedItems.map((item) => (
              <Link className="product-related__card" to={`/kesfet/${item.slug}`} key={item.id}>
                <div className="product-related__media">
                  {catalogCover(item) ? <img src={catalogCover(item)!} alt="" loading="lazy" /> : <span aria-hidden="true">{item.name.charAt(0)}</span>}
                </div>
                <div className="product-related__copy">
                  <small>{localizeCatalogName(item.category, language)}</small>
                  <h3>{item.name}</h3>
                  <p>{item.summary}</p>
                  <span>{c('İncele', 'View details')} <b aria-hidden="true">→</b></span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {p.entitled && p.releases.length > 0 && (
        <Card title={c('Sürümler', 'Releases')} padded={false}>
          <TableWrap caption={c('Sürüm listesi', 'Release list')}>
            <thead>
              <tr>
                <th>{c('Sürüm', 'Version')}</th>
                <th>{c('Kanal', 'Channel')}</th>
                <th className="table__num">{c('Boyut', 'Size')}</th>
                <th>{c('Yayın', 'Published')}</th>
                <th>{c('Notlar', 'Notes')}</th>
              </tr>
            </thead>
            <tbody>
              {p.releases.map((r) => (
                <tr key={r.id}>
                  <td>
                    <strong>v{r.version}</strong>
                  </td>
                  <td>
                    {r.channel === 'beta' ? <Badge tone="warning">beta</Badge> : <Badge>stable</Badge>}
                  </td>
                  <td className="table__num">{formatBytes(r.sizeBytes)}</td>
                  <td>{formatDate(r.publishedAt)}</td>
                  <td className="muted small">{r.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}
    </div>
  );
}

function ProductMediaGallery({
  images,
  name,
  c,
}: {
  images: string[];
  name: string;
  c: (tr: string, en: string) => string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeImage = images[activeIndex] ?? images[0];

  return (
    <div className="product-hero__media">
      {activeImage ? <img src={activeImage} alt={`${name} — ${c('deneyim görseli', 'experience image')}`} /> : <span className="product-hero__media-empty" aria-hidden="true">{name.charAt(0)}</span>}
      {images.length > 1 && (
        <div className="product-hero__gallery" role="group" aria-label={c('Deneyim görselleri', 'Experience images')}>
          {images.map((image, index) => (
            <button
              type="button"
              className={index === activeIndex ? 'is-active' : ''}
              aria-label={c(`${index + 1}. görseli göster`, `Show image ${index + 1}`)}
              aria-pressed={index === activeIndex}
              onClick={() => setActiveIndex(index)}
              key={image}
            >
              <img src={image} alt="" loading={index > 2 ? 'lazy' : 'eager'} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function galleryFor(product: ProductDetailType, banner: string | null, cover: string | null): string[] {
  return [...new Set([
    ...(product.requirements?.galleryUrls ?? []),
    banner,
    cover,
    product.thumbUrl,
  ].filter((value): value is string => Boolean(value)))];
}

function localizedLabel(value: ProductLocalizedLabel | undefined, language: 'tr' | 'en'): string | null {
  const localized = value?.[language]?.trim();
  if (localized) return localized;
  const fallback = value?.tr?.trim();
  return fallback || null;
}

function factsFor(
  product: ProductDetailType,
  language: 'tr' | 'en',
  c: (tr: string, en: string) => string,
): Array<{ label: string; value: string }> {
  const defaults = categoryFacts(product.category, c);
  return [
    {
      label: c('Oturum', 'Session'),
      value: localizedLabel(product.requirements?.sessionDuration, language) ?? defaults.session,
    },
    {
      label: c('Katılımcılar', 'Participants'),
      value: localizedLabel(product.requirements?.participants, language) ?? defaults.participants,
    },
    {
      label: c('Kontrol', 'Controls'),
      value: localizedLabel(product.requirements?.controlScheme, language) ?? defaults.controls,
    },
    {
      label: c('Teslim', 'Delivery'),
      value: product.delivery === 'desktop-install'
        ? c('CORTEX üzerinden', 'Through CORTEX')
        : c('Hub üzerinden', 'Through Hub'),
    },
  ];
}

function categoryFacts(
  category: string,
  c: (tr: string, en: string) => string,
): { session: string; participants: string; controls: string } {
  if (category === 'Leds Play') {
    return {
      session: c('Etkinlik akışına göre', 'Based on the event flow'),
      participants: c('Kurulum kapasitesine göre', 'Based on installation capacity'),
      controls: c('QR ile telefon tarayıcısı', 'QR and mobile browser'),
    };
  }
  if (category === 'VR Deneyimleri') {
    return {
      session: c('Operasyon akışına göre', 'Based on the operation flow'),
      participants: c('VR istasyonu kapasitesine göre', 'Based on VR station capacity'),
      controls: c('Başlık ve hareket kontrolcüleri', 'Headset and motion controllers'),
    };
  }
  if (category === 'AR Deneyimleri') {
    return {
      session: c('Rota veya etkinlik akışına göre', 'Based on route or event flow'),
      participants: c('Alan kapasitesine göre', 'Based on venue capacity'),
      controls: c('Mobil cihaz ve mekânsal etkileşim', 'Mobile device and spatial interaction'),
    };
  }
  return {
    session: c('Mekân akışına göre', 'Based on venue flow'),
    participants: c('Kurulum senaryosuna göre', 'Based on the installation scenario'),
    controls: c('Hareket ve mekânsal algılama', 'Motion and spatial sensing'),
  };
}

function productKindEnglish(kind: string): string {
  return ({ game: 'Game', experience: 'Experience', content: 'Content', service: 'Service' } as Record<string, string>)[kind] ?? kind;
}

function endpointKindEnglish(kind: string): string {
  return ({ led_screen: 'LED screen', vr_station: 'VR station', projection: 'Projection', installation: 'Installation', computer: 'Computer', general: 'General' } as Record<string, string>)[kind] ?? kind;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="row row--between" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 'var(--space-2)' }}>
      <dt className="muted small">{label}</dt>
      <dd style={{ fontWeight: 550 }}>{value}</dd>
    </div>
  );
}
